import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCheck, faTimes, faBuilding, faMapMarkerAlt, 
    faGlobe, faUniversity, faBoxes, faUserTag, faIndustry, 
    faCalendar, faCheckCircle
} from '@fortawesome/free-solid-svg-icons';

const CRMExtractionResults = ({ 
    extractedData, 
    selectedFields, 
    onFieldToggle, 
    onApply, 
    onDiscard 
}) => {
    // Define field configurations for organized display
    const fieldGroups = [
        {
            title: 'Company Information',
            fields: [
                {
                    key: 'legal_entity_name',
                    label: 'Legal Entity Name',
                    icon: faBuilding,
                    required: true
                },
                {
                    key: 'head_office_address',
                    label: 'Head Office Address',
                    icon: faMapMarkerAlt
                },
                {
                    key: 'company_website_url',
                    label: 'Company Website',
                    icon: faGlobe
                },
                {
                    key: 'incorporation_date',
                    label: 'Incorporation Date',
                    icon: faCalendar
                }
            ]
        },
        {
            title: 'Partner Classification',
            fields: [
                {
                    key: 'partner_type_selections',
                    label: 'Type of Partner',
                    icon: faUserTag,
                    isArray: true
                },
                {
                    key: 'industry_selections',
                    label: 'Industries',
                    icon: faIndustry,
                    isArray: true
                }
            ]
        },
        {
            title: 'Trading Information',
            fields: [
                {
                    key: 'traded_commodities',
                    label: 'Traded Commodities',
                    icon: faBoxes,
                    isArray: true
                }
            ]
        },
        {
            title: 'Banking Details',
            fields: [
                {
                    key: 'banking_details_parsed',
                    label: 'Banking Details',
                    icon: faUniversity,
                    isBankingArray: true
                }
            ]
        }
    ];

    // Count non-null fields
    const totalExtractedFields = Object.values(extractedData || {}).filter(v => v !== null).length;
    const selectedCount = Object.values(selectedFields).filter(v => v).length;

    return (
        <div className="crm-document-extraction-results">
            <div className="crm-document-results-header">
                <div className="crm-document-extraction-summary">
                    <FontAwesomeIcon icon={faCheckCircle} className="crm-document-summary-icon" />
                    <div className="crm-document-summary-text">
                        <h3>Extraction Complete</h3>
                        <p>
                            {totalExtractedFields} {totalExtractedFields === 1 ? 'field' : 'fields'} extracted • {' '}
                            {selectedCount} selected for import
                        </p>
                    </div>
                </div>
            </div>

            <div className="crm-document-extraction-instructions">
                <p>Review the extracted data below. Check or uncheck fields to include in your profile.</p>
            </div>

            <div className="crm-document-results-content">
                {fieldGroups.map((group, groupIndex) => {
                    const groupFields = group.fields.filter(field => 
                        extractedData[field.key] !== null && 
                        extractedData[field.key] !== undefined
                    );

                    if (groupFields.length === 0) return null;

                    return (
                        <div key={groupIndex} className="crm-document-field-group">
                            <h4 className="crm-document-field-group-title">{group.title}</h4>
                            <div className="crm-document-field-list">
                                {groupFields.map((field) => {
                                    const value = extractedData[field.key];
                                    const isSelected = selectedFields[field.key];

                                    // Render different value types
                                    let displayValue;
                                    if (field.isBankingArray && Array.isArray(value)) {
                                        // Render banking details as structured cards
                                        displayValue = (
                                            <div className="banking-details-preview">
                                                {value.map((bank, idx) => (
                                                    <div key={idx} className="banking-card-preview">
                                                        <div className="banking-field">
                                                            <strong>Account Owner:</strong> {bank.account_owner_name || 'Not extracted'}
                                                        </div>
                                                        {bank.bank_name && (
                                                            <div className="banking-field">
                                                                <strong>Bank Name:</strong> {bank.bank_name}
                                                            </div>
                                                        )}
                                                        {bank.bank_account_number && (
                                                            <div className="banking-field">
                                                                <strong>Account Number:</strong> {bank.bank_account_number}
                                                            </div>
                                                        )}
                                                        {bank.bank_address_data?.branch_code && (
                                                            <div className="banking-field">
                                                                <strong>Branch Code:</strong> {bank.bank_address_data.branch_code}
                                                            </div>
                                                        )}
                                                        {bank.iban_number && (
                                                            <div className="banking-field">
                                                                <strong>IBAN:</strong> {bank.iban_number}
                                                            </div>
                                                        )}
                                                        {bank.swift_code && (
                                                            <div className="banking-field">
                                                                <strong>SWIFT:</strong> {bank.swift_code}
                                                            </div>
                                                        )}
                                                        {bank.bank_address_data?.formatted_address && (
                                                            <div className="banking-field">
                                                                <strong>Bank Address:</strong> {bank.bank_address_data.formatted_address}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    } else if (field.isArray && Array.isArray(value)) {
                                        // Render arrays as tags
                                        displayValue = (
                                            <div className="tags-preview">
                                                {value.map((item, idx) => (
                                                    <span key={idx} className="tag-badge">{item}</span>
                                                ))}
                                            </div>
                                        );
                                    } else {
                                        // Render as plain text
                                        displayValue = value;
                                    }

                                    return (
                                        <div 
                                            key={field.key} 
                                            className={`crm-document-field-item ${isSelected ? 'selected' : ''} ${field.isBankingArray ? 'banking-array' : ''}`}
                                        >
                                            <div className="crm-document-field-checkbox">
                                                <input
                                                    type="checkbox"
                                                    id={`field-${field.key}`}
                                                    checked={isSelected || false}
                                                    onChange={() => onFieldToggle(field.key)}
                                                    className="crm-document-checkbox-input"
                                                />
                                                <label 
                                                    htmlFor={`field-${field.key}`}
                                                    className="crm-document-checkbox-label"
                                                >
                                                    <FontAwesomeIcon icon={faCheck} className="crm-document-check-icon" />
                                                </label>
                                            </div>
                                            
                                            <div className="crm-document-field-content">
                                                <div className="crm-document-field-header">
                                                    <FontAwesomeIcon 
                                                        icon={field.icon} 
                                                        className="crm-document-field-icon"
                                                    />
                                                    <span className="crm-document-field-label">
                                                        {field.label}
                                                        {field.required && <span className="crm-document-required-badge">Required</span>}
                                                    </span>
                                                </div>
                                                <div className="crm-document-field-value">
                                                    {displayValue}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="crm-document-results-actions">
                <button
                    className="btn btn-secondary"
                    onClick={onDiscard}
                >
                    <FontAwesomeIcon icon={faTimes} />
                    Discard
                </button>
                <button
                    className="btn btn-primary"
                    onClick={onApply}
                    disabled={selectedCount === 0}
                >
                    <FontAwesomeIcon icon={faCheck} />
                    Apply Selected Data ({selectedCount})
                </button>
            </div>

            {selectedCount === 0 && (
                <div className="crm-document-warning-message">
                    Please select at least one field to apply to your profile.
                </div>
            )}
        </div>
    );
};

export default CRMExtractionResults;

